import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, LogOut, User as UserIcon } from 'lucide-react';
import storage from '../utils/storage';
import { hasPermission, getUserPermissions, getUserRole } from '../utils/permissions';
import { initials } from '../utils/jobFormat';

const DEFAULT_LOGO = '/assets/imgs/theme/admin-logo-black.svg';

/**
 * Minimal premium site navigation.
 * Auth state, role-based visibility and candidate permission filtering
 * are unchanged from the previous header — only the presentation is new.
 */
const Header = ({ toggleMobileMenu, mobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const user = storage.getItem('user');
    const token = storage.getItem('token');

    if (user && token) {
      try {
        const userData = JSON.parse(user);
        setUserRole(userData.role);
        setUserName(userData.fullName || userData.companyName || userData.email || '');
        setIsLoggedIn(true);

        if (userData.role === 'candidate') {
          fetchCandidatePhoto(token);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // The header logo is editable by admins (Website Content → Site Logo).
  // Fall back to the bundled logo when no custom logo has been uploaded.
  useEffect(() => {
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_BASE_URL || '';

    const fetchBranding = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/cms/branding`);
        if (!response.ok) return;
        const result = await response.json();
        const headerLogo = result?.success ? result.data?.headerLogo : '';
        if (!cancelled && headerLogo) {
          setLogoUrl(headerLogo.startsWith('http') ? headerLogo : `${baseUrl}${headerLogo}`);
        }
      } catch (error) {
        console.error('Error fetching site logo:', error);
      }
    };

    fetchBranding();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchCandidatePhoto = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/candidates/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.profilePhoto) {
          setProfilePhoto(`${process.env.REACT_APP_BASE_URL}${data.data.profilePhoto}`);
        }
      }
    } catch (error) {
      console.error('Error fetching profile photo:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    storage.removeItem('token');
    storage.removeItem('user');
    setUserRole(null);
    setUserName('');
    setIsLoggedIn(false);
    setShowDropdown(false);
    navigate('/');
    window.location.reload();
  };

  const candidateMenuItems = [
    { path: '/candidate/applied-jobs', label: 'Applied Jobs', permission: 'applications.view' },
    { path: '/candidate/bookmarked-jobs', label: 'Saved Jobs', permission: 'candidate_bookmarks.view' },
    { path: '/candidate/interviews', label: 'Interviews', permission: 'candidate_interviews.view' },
  ];

  const getFilteredCandidateMenu = () => {
    const currentRole = getUserRole();
    const userPermissions = getUserPermissions();

    if (currentRole === 'superadmin' || (currentRole === 'candidate' && userPermissions.length === 0)) {
      return candidateMenuItems;
    }

    return candidateMenuItems.filter((item) => hasPermission(item.permission));
  };

  const filteredCandidateMenu = userRole === 'candidate' ? getFilteredCandidateMenu() : [];
  const isActive = (path) => location.pathname === path;

  return (
    <header className="uc-header">
      <div className="uc-container">
        <div className="uc-header__inner">
          <Link to="/" className="uc-header__brand" aria-label="Home">
            <img
              alt="JobHub"
              src={logoUrl}
              onError={() => setLogoUrl(DEFAULT_LOGO)}
            />
          </Link>

          <nav className="uc-nav" aria-label="Primary">
            {userRole !== 'employer' && (
              <Link
                to="/browse-jobs"
                className={`uc-nav__link${isActive('/browse-jobs') ? ' uc-nav__link--active' : ''}`}
              >
                Browse Jobs
              </Link>
            )}
            <Link
              to="/next-gen-training"
              className={`uc-nav__link${isActive('/next-gen-training') ? ' uc-nav__link--active' : ''}`}
            >
              Next Gen Training
            </Link>
            <Link
              to="/about"
              className={`uc-nav__link${isActive('/about') ? ' uc-nav__link--active' : ''}`}
            >
              About
            </Link>
            <Link
              to="/contact-us"
              className={`uc-nav__link${isActive('/contact-us') ? ' uc-nav__link--active' : ''}`}
            >
              Contact
            </Link>
          </nav>

          <div className="uc-header__actions uc-header__actions--desktop">
            {isLoggedIn && (userRole === 'candidate' || userRole === 'employer') ? (
              <div className="uc-nav__dropdown" ref={dropdownRef}>
                <button
                  type="button"
                  className="uc-avatar-btn"
                  onClick={() => setShowDropdown((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={showDropdown}
                >
                  <span className="uc-avatar">
                    {userRole === 'candidate' && profilePhoto ? (
                      <img src={profilePhoto} alt={userName} />
                    ) : (
                      initials(userName)
                    )}
                  </span>
                  <span className="uc-avatar-name">{userName}</span>
                  <ChevronDown
                    size={15}
                    aria-hidden="true"
                    style={{
                      transition: 'transform 0.2s ease',
                      transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </button>

                {showDropdown && (
                  <ul className="uc-nav__menu" role="menu">
                    <li className="uc-nav__menu-head">
                      <strong>{userName}</strong>
                      <span className="uc-small" style={{ textTransform: 'capitalize' }}>
                        {userRole}
                      </span>
                    </li>

                    <li>
                      <Link
                        role="menuitem"
                        to={userRole === 'employer' ? '/employer/dashboard' : '/candidate/profile'}
                        onClick={() => setShowDropdown(false)}
                      >
                        {userRole === 'employer' ? (
                          <>
                            <LayoutDashboard size={15} aria-hidden="true" /> Employer dashboard
                          </>
                        ) : (
                          <>
                            <UserIcon size={15} aria-hidden="true" /> My profile
                          </>
                        )}
                      </Link>
                    </li>

                    {filteredCandidateMenu.map((item) => (
                      <li key={item.path}>
                        <Link role="menuitem" to={item.path} onClick={() => setShowDropdown(false)}>
                          {item.label}
                        </Link>
                      </li>
                    ))}

                    <li>
                      <button type="button" role="menuitem" onClick={handleLogout}>
                        <LogOut size={15} aria-hidden="true" /> Sign out
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            ) : (
              <>
                {userRole !== 'employer' && (
                  <Link to="/candidate/signin" className="uc-btn uc-btn--outline">
                    Find a job
                  </Link>
                )}
                {userRole !== 'candidate' && (
                  <Link to="/employer/signin" className="uc-btn uc-btn--primary">
                    Hire now
                  </Link>
                )}
              </>
            )}
          </div>

          <button
            type="button"
            className="uc-burger"
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
